'use client';

import { useState } from 'react';
import { Coins, Plus, Loader2 } from 'lucide-react';
import { currencyApi, type CurrencyItem } from '@/app/(modules)/robot-manager/_lib/api';
import { Badge, SimpleListPage } from '@/app/(modules)/robot-manager/_lib/SimpleListPage';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { colors } from '@/styles/theme';
import { toast } from '@/lib/toast';

export default function CurrenciesPage() {
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey((k) => k + 1);

  return (
    <SimpleListPage<CurrencyItem>
      key={refreshKey}
      title="货币 (Currency)"
      description="platform-master L1b · 跨模块复用的货币 master（PO / 报价 / 收款 / 财务报表 都引用）"
      load={async () => currencyApi.list({ enabledOnly: false })}
      searchPlaceholder="搜索 code / name"
      toolbar={<NewCurrencyDialog onCreated={refresh} />}
      columns={[
        { key: 'code', label: 'Code', width: '100px' },
        { key: 'name', label: '名称', width: '200px' },
        { key: 'symbol', label: '符号', width: '80px', render: (c) => c.symbol ?? '-' },
        { key: 'decimals', label: '小数位', width: '80px' },
        {
          key: 'enabled',
          label: '状态',
          width: '100px',
          render: (c) =>
            c.enabled ? (
              <Badge text="启用" bg="#e6fff0" color="#0fc66e" />
            ) : (
              <Badge text="禁用" bg="#f0f0f0" color="#888" />
            ),
        },
        {
          key: '_actions',
          label: '操作',
          width: '160px',
          render: (c) => <CurrencyActions currency={c} onChanged={refresh} />,
        },
      ]}
    />
  );
}

function CurrencyActions({ currency, onChanged }: { currency: CurrencyItem; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    try {
      if (currency.enabled) {
        await currencyApi.disable(currency.code);
        toast.success(`已禁用 ${currency.code}`);
      } else {
        await currencyApi.enable(currency.code);
        toast.success(`已启用 ${currency.code}`);
      }
      onChanged();
    } catch (e: any) {
      toast.error(`操作失败: ${e?.message ?? '未知错误'}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex gap-2">
      <Button variant="outline" size="sm" onClick={toggle} disabled={busy}>
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : currency.enabled ? '禁用' : '启用'}
      </Button>
      <EditCurrencyDialog currency={currency} onSaved={onChanged} />
    </div>
  );
}

function NewCurrencyDialog({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [symbol, setSymbol] = useState('');
  const [decimals, setDecimals] = useState(2);

  const reset = () => {
    setCode('');
    setName('');
    setSymbol('');
    setDecimals(2);
  };

  const handleSubmit = async () => {
    const codeU = code.trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(codeU)) {
      toast.error('Code 必须为 3 位大写字母（ISO 4217），如 USD / CNY / JPY');
      return;
    }
    if (!name.trim()) {
      toast.error('请填写货币名称');
      return;
    }
    setSubmitting(true);
    try {
      await currencyApi.create({ code: codeU, name: name.trim(), symbol: symbol.trim() || undefined, decimals });
      toast.success(`货币 ${codeU} 创建成功`);
      setOpen(false);
      reset();
      onCreated();
    } catch (e: any) {
      toast.error(`创建失败: ${e?.message ?? '未知错误'}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="w-4 h-4 mr-1" />
          新建货币
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>新建货币</DialogTitle>
          <DialogDescription>添加系统支持的新币种（ISO 4217 标准 3 字母代码）</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <Field label="Code (ISO 4217)">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="如 JPY / GBP / KRW"
              maxLength={3}
              disabled={submitting}
            />
          </Field>
          <Field label="名称">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="如 Japanese Yen / 日元"
              disabled={submitting}
            />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="符号">
              <Input
                value={symbol}
                onChange={(e) => setSymbol(e.target.value)}
                placeholder="如 ¥ / $ / €"
                maxLength={8}
                disabled={submitting}
              />
            </Field>
            <Field label="小数位">
              <Input
                type="number"
                min={0}
                max={6}
                value={decimals}
                onChange={(e) => setDecimals(Math.max(0, Math.min(6, parseInt(e.target.value || '2', 10))))}
                disabled={submitting}
              />
            </Field>
          </div>
          <p className="text-xs" style={{ color: colors.textSecondary }}>
            小数位影响金额展示精度：CNY/USD/EUR=2，JPY=0，KWD/BHD=3
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>取消</Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />创建中…</> : '创建'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditCurrencyDialog({ currency, onSaved }: { currency: CurrencyItem; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState(currency.name);
  const [symbol, setSymbol] = useState(currency.symbol ?? '');
  const [decimals, setDecimals] = useState(currency.decimals);

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error('请填写货币名称');
      return;
    }
    setSubmitting(true);
    try {
      await currencyApi.update(currency.code, { name: name.trim(), symbol: symbol.trim() || undefined, decimals });
      toast.success(`货币 ${currency.code} 已更新`);
      setOpen(false);
      onSaved();
    } catch (e: any) {
      toast.error(`更新失败: ${e?.message ?? '未知错误'}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => {
      setOpen(o);
      if (o) {
        setName(currency.name);
        setSymbol(currency.symbol ?? '');
        setDecimals(currency.decimals);
      }
    }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">编辑</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>编辑货币 {currency.code}</DialogTitle>
          <DialogDescription>Code 不可改（已有 FK 引用）</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Field label="名称">
            <Input value={name} onChange={(e) => setName(e.target.value)} disabled={submitting} />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="符号">
              <Input value={symbol} onChange={(e) => setSymbol(e.target.value)} maxLength={8} disabled={submitting} />
            </Field>
            <Field label="小数位">
              <Input
                type="number"
                min={0}
                max={6}
                value={decimals}
                onChange={(e) => setDecimals(Math.max(0, Math.min(6, parseInt(e.target.value || '2', 10))))}
                disabled={submitting}
              />
            </Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>取消</Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />保存中…</> : '保存'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium mb-1 block" style={{ color: colors.textPrimary }}>
        {label}
      </span>
      {children}
    </label>
  );
}
